import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { MessageService } from 'primeng/api';
import { finalize, forkJoin } from 'rxjs';
import { CallReportRow, StaffService, } from 'src/app/api';
import { UiSafeStaff } from 'src/app/api/commonInterfaces';
import { StaffRoleOption, ToolsService } from 'src/app/api/tools.service';
import { DateRangePickerComponent, DateRangePresets } from 'src/app/ui/date-range-picker/date-range-picker.component';
import { UiModule } from 'src/app/ui/ui.module';
import { RecruitingTripReportRow, TripLeaderDetailsRow, TripListRow, NewLeadRow, PaymentReportRow, TripListSummary } from 'src/app/api';
import { Column, DropdownOption } from 'src/app/ui';
import { TableHeadersComponent } from 'src/app/ui/tables';
import { TableSkeletonComponent } from 'src/app/ui/skeletons/table-skeleton/table-skeleton.component';
import { IconService } from 'src/app/ui/icon.service';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { DropdownOptionService } from 'src/app/ui/dropdowns/dropdown-option.service';
import { TableCaptionComponent } from 'src/app/ui/tables';


@Component({
  selector: 'app-reports',
  imports: [CommonModule, UiModule, ReactiveFormsModule, DateRangePickerComponent, TableSkeletonComponent, TableHeadersComponent, TableCaptionComponent],
  standalone: true,
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportsComponent {
  recruitingForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null)
  });

  tripLeadingForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null),
    staffId: new FormControl<number | { id: number } | null>(null)
  });

  tripLeadingDatesForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null)
  });

  webLeadForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null)
  });

  tripPaymentForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null)
  });

  callReportForm = new FormGroup({
    dateRange: new FormControl<[Date, Date] | null>(null),
    callType: new FormControl<number>(0)
  });

  isLoading = signal(false);
  hasSearched = signal(false);

  isLoadingStaffList = signal(false);
  staffList: UiSafeStaff[] = [];
  filteredStaffList = signal<UiSafeStaff[]>([]);
  recruitingTripReport = signal<RecruitingTripReportRow[]>([]);
  tripLeaderDetails = signal<TripLeaderDetailsRow[]>([]);
  atlDetails = signal<TripLeaderDetailsRow[]>([]);
  tripList = signal<TripListRow[]>([]);
  tripListSummary = signal<TripListSummary[]>([]);
  newLeads = signal<NewLeadRow[]>([]);
  paymentReports = signal<PaymentReportRow[]>([]);
  callReportData = signal<CallReportRow[]>([]);
  staffRoleOptions = signal<StaffRoleOption[]>([]);
  callTypesOptions: DropdownOption[] = []
  callReportTotals = signal<{ name: string; total: number; }>({
    name: 'Totals',
    total: 0,
  });

  recruitingTripReportColumns = signal<Column<RecruitingTripReportRow>[]>(
    [
      { field: 'groupname', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custId', tripIdField: 'tripID', sortable: true, filterable: true },
      { field: 'glName', header: 'GL Name', type: 'text', sortable: true, filterable: true },
      { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
      { field: 'tripdate', header: 'Date', type: 'date', sortable: true, filterable: true, dateFormat: 'MMM d, y', },
      { field: 'attendees', header: 'Actuals', type: 'number', sortable: true, filterable: true },
      { field: 'cost', header: 'Cost', type: 'currency', sortable: true, filterable: true },
      { field: 'dateReserved', header: 'Date Reserved', type: 'date', sortable: true, filterable: true, dateFormat: 'MMM d, y', },
      { field: 'attendees_original', header: 'Original Attendees', type: 'number', sortable: true, filterable: true },
      { field: 'recruiterName', header: 'Recruiter Name', type: 'text', sortable: true, filterable: true },
      { field: 'consultantName', header: 'Consultant Name', type: 'text', sortable: true, filterable: true },
      { field: 'tlName', header: 'Trip Leader Name', type: 'text', sortable: true, filterable: true },
      { field: 'custId', header: 'Customer ID', type: 'text', sortable: true, filterable: true },
      { field: 'tripID', header: 'Trip ID', type: 'text', sortable: true, filterable: true },
      { field: 'newTrip', header: 'New Trip', type: 'text', sortable: true, filterable: true }
    ]
  );

  tripLeaderReportColumns = signal<Column<TripLeaderDetailsRow>[]>(
    [
      { field: 'groupname', header: 'Group Name', type: 'text', sortable: true, filterable: true },
      { field: 'leader', header: 'Group Leader', type: 'text', sortable: true, filterable: true },
      { field: 'destination', header: 'Location', type: 'text', sortable: true, filterable: true },
      { field: 'tripdate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
      { field: 'attendees', header: 'Attendees', type: 'number', sortable: true, filterable: true },
    ]
  );

  tripListColumn = signal<Column<TripListRow>[]>(
    [
      { field: 'staffName', header: 'Staff Name', type: 'text', sortable: true, filterable: true },
      { field: 'tripStartDate', header: 'Trip Start Date', type: 'date', sortable: true, filterable: true },
      { field: 'tripEndDate', header: 'Trip End Date', type: 'date', sortable: true, filterable: true },
      { field: 'duration', header: 'Duration (Days)', type: 'number', sortable: true, filterable: true }
    ]
  );

  tripListSummaryColumns = signal<Column<TripListSummary>[]>(
    [
      { field: 'staffName', header: 'Staff Name', type: 'text', sortable: true, filterable: true },
      { field: 'totalTrips', header: 'Total Trips', type: 'number', sortable: true, filterable: true },
      { field: 'totalDays', header: 'Total Days', type: 'number', sortable: true, filterable: true }
    ]
  );

  newLeadColumns = signal<Column<NewLeadRow>[]>(
    [
      { field: 'timestamp', header: 'Date', type: 'date', sortable: true, filterable: true },
      { field: 'size', header: 'Size', type: 'text', sortable: true, filterable: true },
      { field: 'name', header: 'Name', type: 'text', sortable: true, filterable: true },
      { field: 'phone', header: 'Phone', type: 'text', sortable: true, filterable: true },
      { field: 'email', header: 'Email', type: 'text', sortable: true, filterable: true },
      { field: 'org', header: 'Group', type: 'text', sortable: true, filterable: true },
      { field: 'comments', header: 'Comments', type: 'html', sortable: true, filterable: true },
      { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
      { field: 'recruiter', header: 'Recruiter', type: 'text', sortable: true, filterable: true },
      { field: 'type', header: 'Type', type: 'text', sortable: true, filterable: true },
      { field: 'page', header: 'Page', type: 'linkToPpm', linkField: 'page', sortable: true, filterable: true },
    ]
  );

  paymentReportColumns = signal<Column<PaymentReportRow>[]>(
    [
      { field: 'paymentId', header: 'Payment ID', type: 'text', sortable: true, filterable: true },
      { field: 'tripId', header: 'Trip ID', type: 'tripIdWithLink', sortable: true, filterable: true, fragment:'payments', custIdField: 'custID', tripIdField: 'tripId' },
      { field: 'amountPaid', header: 'Amount Paid', type: 'currency', sortable: true, filterable: true },
      { field: 'amountPaidDate', header: 'Payment Date', type: 'date', sortable: true, filterable: true },
      { field: 'quicknote', header: 'Note', type: 'html', sortable: true, filterable: true },
      { field: 'paymentType', header: 'Payment Type', type: 'text', sortable: true, filterable: true },
      { field: 'paymentCheckNumber', header: 'Check Number', type: 'text', sortable: true, filterable: true },
      { field: 'paymentRecievedDate', header: 'Payment Received Date', type: 'date', sortable: true, filterable: true },
      { field: 'tripdate', header: 'Trip Date', type: 'date', sortable: true, filterable: true },
      { field: 'groupname', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custID', tripIdField: 'tripId', sortable: true, filterable: true },
      { field: 'location', header: 'Location', type: 'text', sortable: true, filterable: true },
      { field: 'participants', header: 'Participants', type: 'number', sortable: true, filterable: true },
      { field: 'cost', header: 'Cost', type: 'currency', sortable: true, filterable: true },
      { field: 'totalCharges', header: 'Total Cost', type: 'currency', sortable: true, filterable: true },
      { field: 'totalPaid', header: 'Total Paid', type: 'currency', sortable: true, filterable: true },
      { field: 'remainingBalance', header: 'Remaining Balance', type: 'currency', sortable: true, filterable: true },
      { field: 'datereserved', header: 'Registration Date', type: 'date', sortable: true, filterable: true },
      { field: 'recruiterName', header: 'Recruiter', type: 'text', sortable: true, filterable: true },
      { field: 'consultantName', header: 'Consultant', type: 'text', sortable: true, filterable: true },
      { field: 'tlName', header: 'Trip Leader', type: 'text', sortable: true, filterable: true }
    ]
  );

  callReportColumns = signal<Column<CallReportRow>[]>(
    [
      { field: 'name', header: 'Staff Name', type: 'text', sortable: true, filterable: true },
      { field: 'total', header: 'Total Calls', type: 'number', sortable: true, filterable: true },
      { field: 'successful', header: 'Successful', type: 'number', sortable: true, filterable: true },
      { field: 'message', header: 'Message', type: 'number', sortable: true, filterable: true },
      { field: 'attempted', header: 'Attempted', type: 'number', sortable: true, filterable: true },
      { field: 'unknown', header: 'Unknown', type: 'number', sortable: true, filterable: true }
    ]
  )

  constructor(
    private staffService: StaffService,
    private toolsService: ToolsService,
    private router: Router,
    private messageService: MessageService,
    private dropdownOptionService: DropdownOptionService,
    public icons: IconService,
    private auth: LocalAuthService
  ) { }

  ngOnInit(): void {
    this.loadDropdownOptions();

    this.tripLeadingForm.get('dateRange')?.valueChanges.subscribe(dateRange => {
      if (dateRange && dateRange[0] && dateRange[1]) {
        this.updateTripLeadersForDateRange(
          dateRange[0].toISOString().split('T')[0],
          dateRange[1].toISOString().split('T')[0]
        );
      } else {
        // If no date range is selected, show all trip leaders
        this.filteredStaffList.set(this.staffList);
      }
    });

  }

  tripDatePresets: DateRangePresets[] = [
    { label: "This Year", value: [DateTime.now().startOf("year").toJSDate(), DateTime.now().endOf("year").toJSDate()] },
    { label: "Last Year", value: [DateTime.now().minus({ years: 1 }).startOf("year").toJSDate(), DateTime.now().minus({ years: 1 }).endOf("year").toJSDate()] },
    { label: "Last 6 months", value: [DateTime.now().minus({ months: 6 }).startOf("day").toJSDate(), DateTime.now().endOf("day").toJSDate()] },
    { label: "Last 3 months", value: [DateTime.now().minus({ months: 3 }).startOf("day").toJSDate(), DateTime.now().endOf("day").toJSDate()] }
  ];

  get isSalesAdmin(): boolean {
    return this.auth.hasRole('salesAdmin');
  }

  private loadDropdownOptions() {
    this.staffService.getTripLeaderList().subscribe(staff => {
      this.staffList = staff;
      this.filteredStaffList.set(staff);
    });

    this.callTypesOptions = this.dropdownOptionService.CallTypes()
  }

  private updateTripLeadersForDateRange(fromDate: string, toDate: string): void {
    this.isLoadingStaffList.set(true);
    this.staffService.getTripLeadersByDateRange(fromDate, toDate)
      .pipe(finalize(() => this.isLoadingStaffList.set(false)))
      .subscribe({
        next: (staff) => {
          this.filteredStaffList.set(staff);

          const currentStaffId = this.getCurrentStaffId();
          if (currentStaffId && !staff.some(s => s.id === currentStaffId)) {
            this.tripLeadingForm.get('staffId')?.reset();
          }
        },
        error: (error) => {
          console.error('Error fetching trip leaders by date range:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load trip leaders for selected date range',
            life: 3000
          });
          this.filteredStaffList.set(this.staffList);
        }
      });
  }

  private getCurrentStaffId(): number | null {
    const staffId = this.tripLeadingForm.get('staffId')?.value;
    if (staffId && typeof staffId === 'object' && 'id' in staffId) {
      return staffId.id;
    } else if (typeof staffId === 'number') {
      return staffId;
    }
    return null;
  }

  onSubmitRecruitingReport(): void {

    this.clearAllFormsExcept('recruiting');

    const { dateRange } = this.recruitingForm.value;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both from and to dates',
        life: 3000
      });
      return;
    }

    this.clearAllReportData();
    this.isLoading.set(true);
    this.recruitingTripReport.set([]);

    this.toolsService.getRecruitingTripReport(
      dateRange[0].toISOString().split('T')[0],
      dateRange[1].toISOString().split('T')[0]
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (data) => {
        this.recruitingTripReport.set(data);
        this.hasSearched.set(true);

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: 'Recruiting report has been loaded',
          life: 3000
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate recruiting report',
          life: 3000
        });
      }
    });
  }

  onSubmitTripLeadingReport(): void {
    this.clearAllFormsExcept('tripLeading');

    const { staffId, dateRange } = this.tripLeadingForm.value;

    let staffIdToUse: number = 0;

    if (staffId && typeof staffId === 'object' && 'id' in staffId) {
      staffIdToUse = staffId.id;
    } else if (typeof staffId === 'number') {
      staffIdToUse = staffId;
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select a date range',
        life: 3000
      });
      return;
    }


    this.clearAllReportData();
    this.isLoading.set(true);
    this.tripLeaderDetails.set([]);
    this.atlDetails.set([]);

    const fromDate = dateRange[0].toISOString().split('T')[0];
    const toDate = dateRange[1].toISOString().split('T')[0];

    const tlDetailsObs = this.toolsService.getTripLeaderDetails(staffIdToUse, fromDate, toDate);
    const atlDetailsObs = this.toolsService.getAtlDetails(staffIdToUse, fromDate, toDate);

    forkJoin({
      tripLeader: tlDetailsObs,
      atl: atlDetailsObs
    }).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (results) => {
        this.tripLeaderDetails.set(results.tripLeader);
        this.atlDetails.set(results.atl);
        this.hasSearched.set(true);

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: 'Trip leader and ATL reports have been loaded',
          life: 3000
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate trip leader/ATL reports',
          life: 3000
        });
      }
    });
  }

  onSubmitTripLeadingDatesReport(): void {

    this.clearAllFormsExcept('tripLeadingDates');

    const { dateRange } = this.tripLeadingDatesForm.value;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both from and to dates',
        life: 3000
      });
      return;
    }

    this.clearAllReportData();
    this.isLoading.set(true);
    this.tripList.set([]);
    this.tripListSummary.set([]);

    this.toolsService.getTripList(
      dateRange[0].toISOString().split('T')[0],
      dateRange[1].toISOString().split('T')[0]
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (data) => {
        this.tripList.set(data);
        this.hasSearched.set(true);
        const summaryMap = new Map<number, TripListSummary>();

        data.forEach(trip => {
          if (!summaryMap.has(trip.staffId)) {
            summaryMap.set(trip.staffId, {
              staffId: trip.staffId,
              staffName: trip.staffName,
              totalTrips: 0,
              totalDays: 0
            });
          }

          const summary = summaryMap.get(trip.staffId)!;
          summary.totalTrips += 1;
          summary.totalDays += trip.duration;
        });

        this.tripListSummary.set(Array.from(summaryMap.values()));

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: 'Staff trip leadership report has been loaded',
          life: 3000
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate staff trip leadership report',
          life: 3000
        });
      }
    });
  }

  onSubmitWebLeadReport(): void {

    this.clearAllFormsExcept('webLead');

    const { dateRange } = this.webLeadForm.value;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both from and to dates',
        life: 3000
      });
      return;
    }

    this.clearAllReportData();
    this.isLoading.set(true);
    this.newLeads.set([]);

    this.toolsService.getNewLeads(
      dateRange[0].toISOString().split('T')[0],
      dateRange[1].toISOString().split('T')[0]
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (data) => {
        this.newLeads.set(data);
        this.hasSearched.set(true);

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: 'Web lead report has been loaded',
          life: 3000
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate web lead report',
          life: 3000
        });
      }
    });
  }

  onSubmitTripPaymentReport(): void {

    this.clearAllFormsExcept('tripPayment');

    const { dateRange } = this.tripPaymentForm.value;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both from and to dates',
        life: 3000
      });
      return;
    }

    this.clearAllReportData();
    this.isLoading.set(true);
    this.paymentReports.set([]);

    this.toolsService.getPayments(
      dateRange[0].toISOString().split('T')[0],
      dateRange[1].toISOString().split('T')[0]
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (data) => {
        this.paymentReports.set(data);
        this.hasSearched.set(true);

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: 'Trip payment report has been loaded',
          life: 3000
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate trip payment report',
          life: 3000
        });
      }
    });
  }

  onSubmitCallReport(): void {

    this.clearAllFormsExcept('callReport');

    const { dateRange, callType } = this.callReportForm.value;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select both from and to dates',
        life: 3000
      });
      return;
    }

    this.clearAllReportData();
    this.isLoading.set(true);
    this.callReportData.set([]);

    const fromDate = dateRange[0].toISOString().split('T')[0];
    const toDate = dateRange[1].toISOString().split('T')[0];

    this.toolsService.getCallReport(
      fromDate,
      toDate,
      callType || 0
    ).pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: (data) => {
        this.callReportData.set(data);
        this.hasSearched.set(true);

        let totalCalls = 0;
        data.forEach(row => {
          totalCalls += Number(row.total || 0);
        });

        this.callReportTotals.set({
          name: this.callReportTotals().name,
          total: totalCalls,
        });

        this.messageService.add({
          severity: 'success',
          summary: 'Report Generated',
          detail: `Call report has been loaded with ${data.length} records`,
          life: 3000
        });
      },
      error: (error) => {
        console.error('Error generating call report:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to generate call report',
          life: 3000
        });
      }
    });
  }

  clearAllReportData(): void {
    this.recruitingTripReport.set([]);
    this.tripLeaderDetails.set([]);
    this.atlDetails.set([]);
    this.tripList.set([]);
    this.tripListSummary.set([]);
    this.newLeads.set([]);
    this.paymentReports.set([]);
    this.callReportData.set([]);
    this.callReportTotals.set({
      name: 'Totals',
      total: 0,
    });
  }


  hasAnyData(): boolean {
    return this.recruitingTripReport().length > 0 ||
      this.tripLeaderDetails().length > 0 ||
      this.atlDetails().length > 0 ||
      this.tripList().length > 0 ||
      this.newLeads().length > 0 ||
      this.paymentReports().length > 0 ||
      this.callReportData().length > 0;
  }

  clearTripLeadingForm(): void {
    this.tripLeadingForm.reset();
    this.filteredStaffList.set(this.staffList);
  }

  clearAllFormsExcept(formToKeep: string): void {
    if (formToKeep !== 'recruiting') {
      this.recruitingForm.reset();
    }

    if (formToKeep !== 'tripLeading') {
      this.tripLeadingForm.reset();
    }

    if (formToKeep !== 'tripLeadingDates') {
      this.tripLeadingDatesForm.reset();
    }

    if (formToKeep !== 'webLead') {
      this.webLeadForm.reset();
    }

    if (formToKeep !== 'tripPayment') {
      this.tripPaymentForm.reset();
    }

    if (formToKeep !== 'callReport') {
      this.callReportForm.reset();
    }
  }

}
